import React from "react";
import {
  DispatchGanttTimeline,
  PreviewBoundary,
  buildDispatchGanttComparison,
} from "./consolidation/DispatchGanttPreview.jsx";
import { fetchTruckOptimizationGanttCapture } from "../lib/systemConfigApi.js";

const SIDES = [["before", "Avant optimisation"], ["after", "Après optimisation"]];
// Photos are immutable: refreshing the Captures list must not download them again.
const photoRequests = new Map();

function loadPhoto(captureId) {
  if (!photoRequests.has(captureId)) {
    const request = fetchTruckOptimizationGanttCapture(captureId);
    request.catch(() => photoRequests.delete(captureId));
    photoRequests.set(captureId, request);
  }
  return photoRequests.get(captureId);
}

function Comparison({ photo, dispatchDate, dispatchPeriod, fallback }) {
  const model = React.useMemo(() => buildDispatchGanttComparison([photo.before, photo.after], dispatchDate, dispatchPeriod),
    [photo, dispatchDate, dispatchPeriod]);
  if (!model) return fallback;
  return (
    <div className="grid min-w-0 gap-2" data-truck-gantt-comparison="ready">
      {SIDES.map(([side, title], index) => (
        <div className="min-w-0 rounded-[4px] border border-slate-200 bg-slate-50/80 p-2" key={side} data-truck-gantt-side={side}>
          <h4 className="mb-1 text-xs font-bold text-slate-900">{title}</h4>
          <DispatchGanttTimeline row={model.rows[index]} bounds={model.bounds} layout={model.layout}
            dispatchDate={dispatchDate} dispatchPeriod={dispatchPeriod}
            label={`${title} · ${model.rows[index].truckLabel || photo.truck_id || ""}`} />
        </div>
      ))}
    </div>
  );
}

export default function TruckOptimizationGanttComparison({ captureId, dispatchDate, dispatchPeriod, fallback }) {
  const placeholderRef = React.useRef(null);
  const [visible, setVisible] = React.useState(false);
  const [state, setState] = React.useState({ status: "loading", photo: null });

  React.useEffect(() => {
    const placeholder = placeholderRef.current;
    const IntersectionObserverImpl = typeof window !== "undefined" ? window.IntersectionObserver : undefined;
    if (!placeholder || typeof IntersectionObserverImpl !== "function") {
      setVisible(true);
      return undefined;
    }
    // A long Captures list only downloads the photos near the viewport.
    const observer = new IntersectionObserverImpl((entries) => {
      if (!entries.some((entry) => entry.isIntersecting || entry.intersectionRatio > 0)) return;
      observer.disconnect();
      setVisible(true);
    }, { rootMargin: "480px 0px" });
    observer.observe(placeholder);
    return () => observer.disconnect();
  }, [captureId]);

  React.useEffect(() => {
    if (!visible) return undefined;
    let active = true;
    setState({ status: "loading", photo: null });
    loadPhoto(captureId).then(
      (photo) => active && setState({ status: "ready", photo }),
      () => active && setState({ status: "failed", photo: null }),
    );
    return () => { active = false; };
  }, [captureId, visible]);

  if (state.status === "failed") return fallback;
  if (state.status !== "ready") {
    return (
      <p ref={placeholderRef} className="m-0 rounded-[4px] border border-slate-200 bg-slate-50/80 px-2 py-1.5 text-xs text-slate-500"
        data-truck-gantt-comparison="loading">
        Chargement du Gantt…
      </p>
    );
  }
  const { photo } = state;
  return (
    <PreviewBoundary assignment={photo} fallback={fallback}>
      <Comparison photo={photo} dispatchDate={photo.dispatch_date || dispatchDate}
        dispatchPeriod={photo.dispatch_period || dispatchPeriod} fallback={fallback} />
    </PreviewBoundary>
  );
}
